import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { NationallyRecognisedTrainingLogoBlack } from '../PdfLayout';
import { CourseCard } from './CourseCard';
import { QRWithLogo } from '../QRWithLogo';
import CourseCatalogProps, { Course } from './types';
import { PinkLogo } from '../PinkLogo';

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 52,
    paddingBottom: 100,
    fontFamily: 'Inter',
    fontSize: 12,
  },
  header: { textAlign: 'center', alignItems: 'center' },
  logoCentered: { alignSelf: 'center' },
  headerTitle: {
    fontSize: 18,
    color: '#171923',
    marginTop: 8,
    marginBottom: 30,
    lineHeight: '23px',
  },
  headerSubtitle: {
    fontSize: 17,
    color: '#4A5568',
    marginBottom: 24,
    marginTop: -20,
    textAlign: 'center',
    lineHeight: '22px',
  },
  priceRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
    marginBottom: 16,
  },
  priceText: {
    fontSize: 11,
    color: '#171923',
    lineHeight: '14px',
  },
  priceSubText: { fontSize: 10 },
  bold: { fontFamily: 'Inter-SemiBold' },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 70,
    right: 70,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 10,
    color: '#171923',
  },
  footerCenter: { flexDirection: 'column', alignItems: 'center' },
  footerText: { fontSize: 10 },
  footerRto: {
    color: '#4A5568',
    fontSize: 10,
    lineHeight: '13px',
    paddingTop: 16,
  },
  qrContainer: { width: 55, height: 55 },
});

function Footer() {
  return (
    <View style={styles.footer} fixed>
      <NationallyRecognisedTrainingLogoBlack />
      <View style={styles.footerCenter}>
        <Text style={styles.footerText}>www.duke.co</Text>
        <Text style={styles.footerText}>1300 DUKE CO</Text>
        <Text style={styles.footerText}>support@duke.co</Text>
        <Text style={styles.footerRto}>Duke Education | RTO #22497</Text>
      </View>
      <View style={styles.qrContainer}>
        <QRWithLogo />
      </View>
    </View>
  );
}

function CertificatePage({
  courses,
  title,
  subtitle,
}: {
  courses: Course[];
  title: string;
  subtitle: string;
}) {
  const course = courses[0];

  return (
    <Page size="A4" style={styles.page} wrap>
      <View style={[styles.header]} fixed>
        <View style={styles.logoCentered}>
          <PinkLogo />
        </View>
        <Text style={styles.headerTitle}>{title}</Text>
      </View>

      <Text style={styles.headerSubtitle}>{subtitle}</Text>

      {course?.semesterPrice && (
        <View style={styles.priceRow}>
          <Text style={styles.priceText}>
            <Text style={styles.bold}>${course?.semesterPrice}</Text> Single Semester
          </Text>
          <Text style={styles.priceSubText}>
            <Text style={styles.bold}>${course?.fullCoursePrice}</Text> Full Course
          </Text>
          <Text style={styles.priceSubText}>
            Duration <Text style={styles.bold}>{course?.duration}</Text>
          </Text>
        </View>
      )}

      <View style={styles}>
        {courses.map((c) => (
          <CourseCard key={`${c.code}-${c.title}`} course={c} />
        ))}
      </View>

      <Footer />
    </Page>
  );
}

function SkillSetPage({ courses }: { courses: Course[] }) {
  return (
    <Page size="A4" style={styles.page} wrap>
      <View style={[styles.header]} fixed>
        <View style={styles.logoCentered}>
          <PinkLogo />
        </View>
        <Text style={styles.headerTitle}>Chat, learn, succeed</Text>
      </View>

      <Text style={styles.headerSubtitle}>
        Build your skills with our Nationally Accredited Skillsets
      </Text>

      <View>
        {courses.map((course) => (
          <CourseCard key={`${course.code}-${course.title}`} course={course} />
        ))}
      </View>

      <Footer />
    </Page>
  );
}

function NonVocPage({ courses }: { courses: Course[] }) {
  return (
    <Page size="A4" style={styles.page} wrap>
      <View style={[styles.header]} fixed>
        <View style={styles.logoCentered}>
          <PinkLogo />
        </View>
        <Text style={styles.headerTitle}>Chat, learn, succeed</Text>
      </View>

      <Text style={styles.headerSubtitle}>Practical training for job readiness</Text>

      <View>
        {courses.map((course) => (
          <CourseCard key={`${course.code}-${course.title}`} course={course} />
        ))}
      </View>

      <Footer />
    </Page>
  );
}

function ShortCoursesPage({ courses }: { courses: Course[] }) {
  return (
    <Page size="A4" style={styles.page} wrap>
      <View style={[styles.header]} fixed>
        <View style={styles.logoCentered}>
          <PinkLogo />
        </View>
        <Text style={styles.headerTitle}>Chat, learn, succeed</Text>
      </View>

      <Text style={styles.headerSubtitle}>Enhance your skills with our short courses</Text>

      <View>
        {courses.map((course) => (
          <CourseCard key={`${course.code}-${course.title}`} course={course} />
        ))}
      </View>

      <Footer />
    </Page>
  );
}

function CourseCatalog({ data }: { data: CourseCatalogProps }): React.ReactElement {
  const qualifications = data.courses.filter((c) => c.kind === 'QUALIFICATION');

  const certII = qualifications
    .filter((c) => c.qualificationKind === 'CERT_II' || getQualificationKind(c.title) === 'CERT_II')
    .sort((a, b) => a.title.localeCompare(b.title));
  const certIII = qualifications
    .filter(
      (c) => c.qualificationKind === 'CERT_III' || getQualificationKind(c.title) === 'CERT_III',
    )
    .sort((a, b) => a.title.localeCompare(b.title));
  const certIV = qualifications
    .filter((c) => c.qualificationKind === 'CERT_IV' || getQualificationKind(c.title) === 'CERT_IV')
    .sort((a, b) => a.title.localeCompare(b.title));
  const skillSet = data.courses
    .filter((c) => c.kind === 'SKILL_SET')
    .sort((a, b) => a.title.localeCompare(b.title));
  const nonVoc = data.courses
    .filter((c) => c.kind === 'NON_VOCATIONAL')
    .sort((a, b) => a.title.localeCompare(b.title));
  const shortCourses = data.courses
    .filter((c) => c.kind === 'SHORT_COURSE')
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <>
      {certIV.length > 0 && (
        <CertificatePage
          courses={certIV}
          title="Chat, learn, succeed"
          subtitle="Start your career journey with our Certificate IV courses"
        />
      )}
      {certIII.length > 0 && (
        <CertificatePage
          courses={certIII}
          title="Chat, learn, succeed"
          subtitle="Start your career journey with our Certificate III courses"
        />
      )}
      {certII.length > 0 && (
        <CertificatePage
          courses={certII}
          title="Chat, learn, succeed"
          subtitle="Unlock your career potential with our Certificate II courses"
        />
      )}
      {skillSet.length > 0 && <SkillSetPage courses={skillSet} />}
      {nonVoc.length > 0 && <NonVocPage courses={nonVoc} />}
      {shortCourses.length > 0 && <ShortCoursesPage courses={shortCourses} />}
    </>
  );
}

export default CourseCatalog;

// Workaround to group qualifications using their title until qualificationKind is available
function getQualificationKind(courseTitle: string): string | null {
  const title = courseTitle.toLowerCase();

  if (/certificate\s+iv\b/.test(title)) {
    return 'CERT_IV';
  }
  if (/certificate\s+iii\b/.test(title)) {
    return 'CERT_III';
  }
  if (/certificate\s+ii\b/.test(title)) {
    return 'CERT_II';
  }

  return null;
}
